/* ------------------------------------------------------------------ */
/*  src/store/api/dashboardApi.js – unified baseQuery version          */
/* ------------------------------------------------------------------ */
import { baseApi } from "./baseApi";
import {
  setStats,
  setChartData,
  setLeaderboards,
  setLoading,
  setError,
} from "../slices/dashboardSlice";
import { USER_ROLES } from "../../utils/constants";

/* ---------- tiny helpers ----------------------------------------- */
const N = (v, d = 0) => (v == null || v === "" ? d : Number(v) || d);

const qs = (obj) =>
  Object.entries(obj ?? {})
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

let latestMetricsRequestId = 0;

// Helper to adjust date range for proper filtering
// Start date: beginning of the day (00:00:00.000)
// End date: end of the day (23:59:59.999)
const adjustDateRange = (startStr, endStr) => {
  if (!startStr && !endStr) return { from: null, to: null };

  const parseLocalDate = (value) => {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(value);
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  };

  const startDate = startStr ? parseLocalDate(startStr) : null;
  const endDate = endStr ? parseLocalDate(endStr) : null;

  startDate?.setHours(0, 0, 0, 0);
  endDate?.setHours(23, 59, 59, 999);
  
  return {
    from: startDate?.toISOString(),
    to: endDate?.toISOString()
  };
};

/* ------------------------------------------------------------------ */
/*  getMetrics – uses baseQuery passed by RTK-Query                   */
/* ------------------------------------------------------------------ */
const getMetricsQueryFn = async (
  filters = {},
  { dispatch, getState },
  _extra,
  baseQuery
) => {
  const requestId = ++latestMetricsRequestId;
  dispatch(setLoading(true));
  dispatch(setError(null));
  const role = getState?.()?.auth?.role;
  const isSuperAdmin = role === USER_ROLES.SUPER_ADMIN;

  /* -------- build query string from filters --------------------- */
  const params = {};
  if (filters.serviceCode?.trim()) params.service_code = filters.serviceCode.trim();
  if (filters.platform?.trim()) params.platform = filters.platform.trim();
  if (filters.resultFilter?.trim()) params.result_filter = filters.resultFilter.trim();

  if (filters.dateRange?.start || filters.dateRange?.end) {
    const { from, to } = adjustDateRange(filters.dateRange.start, filters.dateRange.end);
    params.from = from;
    params.to = to;
  } else {
    params.period =
      {
        today: "daily",
        weekly: "weekly",
        monthly: "monthly",
        yearly: "yearly",
        all_time: "all_time",
      }[filters.quickFilter?.toLowerCase()] || "daily";
  }
  const qsStr = qs(params);

  /* -------- helper that calls the shared baseQuery -------------- */
  const bGet = async (url) => {
    const res = await baseQuery({ url });
    if (res.error) throw new Error(res.error.data ?? res.error.status);
    return res.data;
  };

  try {
    /* parallel requests, all via baseQueryWithReauth */
    const [rawStats, rawChart, winners, byPoints, topPlayers, byGames] =
      await Promise.all([
        bGet(`/data/stats?${qsStr}`),
        bGet(`/data/revenue-breakdown?${qsStr}&groupBy=day&limit=30`),
        bGet(`/data/top-winners?${qsStr}&limit=10`),
        bGet(`/data/top-players-points?${qsStr}&limit=10`),
        bGet(`/data/top-players-bet-amount?${qsStr}&limit=10`),
        bGet(`/data/top-active-players?${qsStr}&limit=10`),
      ]);

    if (requestId !== latestMetricsRequestId) {
      return { data: { stale: true } };
    }

    /* ---- transforms ------------------------------------------- */
    const stats = {
      totalPlay: N(rawStats.total_play_amount),
      totalWin: N(rawStats.total_winnings),
      customers: N(rawStats.total_customers),
      newCustomers: N(rawStats.new_customers),
      existingCustomers: N(rawStats.existing_customers),
      revenue: N(
        isSuperAdmin
          ? (rawStats.net_revenue ?? rawStats.revenue)
          : (rawStats.gross_revenue ?? rawStats.revenue)
      ),
      grossRevenue: N(rawStats.gross_revenue ?? rawStats.revenue),
      netRevenue: N(
        isSuperAdmin
          ? (rawStats.net_revenue ?? rawStats.revenue)
          : (rawStats.gross_revenue ?? rawStats.revenue)
      ),
      jackpotContribution: N(isSuperAdmin ? rawStats.jackpot_contribution : 0),
      totalGames: N(rawStats.total_games_played),
      totalBets: N(rawStats.total_bets),
      avgBetAmount: N(rawStats.avg_bet_amount),
      avgWinAmount: N(rawStats.avg_win_amount),
      winPercentage: N(rawStats.win_percentage),
    };

    const chronologicalChart = [...(rawChart || [])].sort((a, b) =>
      String(a.period).localeCompare(String(b.period))
    );

    const chartData = isSuperAdmin
      ? [
          {
            id: "Net Revenue",
            data: chronologicalChart.map(({ period, net_revenue, revenue }) => ({
              x: period,
              y: N(net_revenue ?? revenue),
            })),
          },
          {
            id: "Gross Revenue",
            data: chronologicalChart.map(({ period, gross_revenue, revenue }) => ({
              x: period,
              y: N(gross_revenue ?? revenue),
            })),
          },
        ]
      : [
          {
            id: "Revenue",
            data: chronologicalChart.map(({ period, gross_revenue, revenue }) => ({
              x: period,
              y: N(gross_revenue ?? revenue),
            })),
          },
        ];

    const mkRow = (p, i) => ({
      id: N(p.rank, i + 1),
      phone: p.phone || "N/A",
      user_id: p.user_id || "",
      points: N(p.total_points), // Add this line
      play: N(p.total_bet_amount),
      win: N(p.total_winnings),
      games: N(p.games_played || p.total_bets),
    });

    /* ---- update slice ----------------------------------------- */
    dispatch(setStats(stats));
    dispatch(setChartData(chartData));
    dispatch(
      setLeaderboards({
        topWinners: winners.map(mkRow),
        topByPoints: byPoints.map(mkRow),
        topPlayers: topPlayers.map(mkRow),
        topByGames: byGames.map(mkRow),
      })
    );

    return { data: { stats, chartData } };
  } catch (err) {
    if (requestId === latestMetricsRequestId) {
      dispatch(setError(err.message || "Failed to fetch metrics"));
    }
    return { error: err.toString() };
  } finally {
    if (requestId === latestMetricsRequestId) {
      dispatch(setLoading(false));
    }
  }
};

/* ------------------------------------------------------------------ */
/*  Other endpoints – still use normal query/transform                */
/* ------------------------------------------------------------------ */
export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMetrics: builder.query({
      queryFn: getMetricsQueryFn,
      providesTags: ["Dashboard"],
    }),

    getServiceCodes: builder.query({
      query: () => "/data/service-codes",
      transformResponse: (res) =>
        (Array.isArray(res) ? res : [res]).map((s) => ({
          value: s.service_code === "all" ? "" : String(s.service_code || ""),
          label:
            s.service_code === "all"
              ? "All Services"
              : String(s.service_code || ""),
          bet_count: N(s.bet_count),
          total_amount: N(s.total_amount),
        })),
      providesTags: ["Dashboard"],
    }),
    

    getCustomers: builder.query({
      query: (params = {}) => {
        // Enhanced parameter handling for phone search
        const queryParams = { ...params };

        // Handle phone number search
        if (params.phone && params.phone.trim()) {
          queryParams.phone = params.phone.trim();
        }

        // Handle other existing filters
        if (params.serviceCode && params.serviceCode.trim()) {
          queryParams.service_code = params.serviceCode.trim();
        }

        // Handle date range filters
        if (params.dateRange?.start && params.dateRange.end) {
          const { from, to } = adjustDateRange(params.dateRange.start, params.dateRange.end);
          queryParams.from = from;
          queryParams.to = to;
        } else if (params.quickFilter) {
          queryParams.period =
            {
              today: "daily",
              weekly: "weekly",
              monthly: "monthly",
              yearly: "yearly",
            }[params.quickFilter.toLowerCase()] || "all_time";
        }

        // Handle pagination
        if (params.limit) {
          queryParams.limit = params.limit;
        }
        if (params.offset) {
          queryParams.offset = params.offset;
        }

        console.log("Customer search params:", queryParams);
        return `/data/customers?${qs(queryParams)}`;
      },
      transformResponse: (res) => {
        console.log("Raw customers API response:", res);

        if (!res) {
          console.warn("No response from customers API");
          return {
            summary: {
              totalCustomers: 0,
              totalPlayAmount: 0,
              totalWinnings: 0,
              avgCustomerValue: 0,
              groupedBy: "phone",
            },
            customers: [],
          };
        }

        const transformedData = {
          summary: {
            totalCustomers: N(res?.summary?.total_customers),
            totalPlayAmount: N(res?.summary?.total_play_amount),
            totalWinnings: N(res?.summary?.total_winnings),
            avgCustomerValue: N(res?.summary?.avg_customer_value),
            groupedBy: res?.summary?.grouped_by || "phone",
          },
          customers: (res?.customers || []).map((customer) => ({
            phone: customer.phone || "N/A",
            userId: customer.user_id || "",
            totalBets: N(customer.total_bets),
            totalPlayAmount: N(customer.total_play_amount),
            totalWinnings: N(customer.total_winnings),
            netLoss: N(customer.net_loss),
            avgBetAmount: N(customer.avg_bet_amount),
            firstBetDate: customer.first_bet_date || "",
            lastBetDate: customer.last_bet_date || "",
            uniqueGamesPlayed: N(customer.unique_games_played),
            gamesList: customer.games_list || "",
            winningBets: N(customer.winning_bets),
            winRatePercentage: N(customer.win_rate_percentage),
            filteredService: customer.filtered_service || "all",
          })),
        };

        console.log("Transformed customers data:", transformedData);
        return transformedData;
      },
      providesTags: ["Dashboard"],
    }),

    // New endpoint specifically for phone number search
    searchCustomerByPhone: builder.query({
      query: (phoneNumber) => {
        if (!phoneNumber || !phoneNumber.trim()) {
          throw new Error("Phone number is required for search");
        }

        const params = {
          phone: phoneNumber.trim(),
          limit: 1, // Usually searching for a specific customer
        };

        console.log("Searching customer by phone:", phoneNumber);
        return `/data/customers?${qs(params)}`;
      },
      transformResponse: (res) => {
        console.log("Phone search API response:", res);

        if (!res || !res.customers || res.customers.length === 0) {
          return {
            found: false,
            customer: null,
            message: "No customer found with this phone number",
          };
        }

        const customer = res.customers[0]; // Get first (should be only) result
        return {
          found: true,
          customer: {
            phone: customer.phone || "N/A",
            userId: customer.user_id || "",
            totalBets: N(customer.total_bets),
            totalPlayAmount: N(customer.total_play_amount),
            totalWinnings: N(customer.total_winnings),
            netLoss: N(customer.net_loss),
            avgBetAmount: N(customer.avg_bet_amount),
            firstBetDate: customer.first_bet_date || "",
            lastBetDate: customer.last_bet_date || "",
            uniqueGamesPlayed: N(customer.unique_games_played),
            gamesList: customer.games_list || "",
            winningBets: N(customer.winning_bets),
            winRatePercentage: N(customer.win_rate_percentage),
            filteredService: customer.filtered_service || "all",
          },
          summary: res.summary,
        };
      },
      providesTags: ["Dashboard"],
    }),

    getRevenueBreakdown: builder.query({
      query: (p) => `/data/revenue-breakdown?${qs(p)}`,
      transformResponse: (arr) =>
        (arr || []).map((d, i) => ({
          period: d.period || `P${i + 1}`,
          revenue: N(d.revenue),
          totalBets: N(d.total_bets),
          totalPlayAmount: N(d.total_play_amount),
          totalWinnings: N(d.total_winnings),
          uniqueCustomers: N(d.unique_customers),
          avgBetAmount: N(d.avg_bet_amount),
        })),
      providesTags: ["Dashboard"],
    }),

    getGameStats: builder.query({
      query: (p) => `/data/games?${qs(p)}`,
      transformResponse: (arr) =>
        (arr || []).map((g) => ({
          serviceCode: g.service_code || "Unknown",
          totalBets: N(g.total_bets),
          totalPlayAmount: N(g.total_play_amount),
          totalWinnings: N(g.total_winnings),
          revenue: N(g.revenue),
          uniquePlayers: N(g.unique_players),
          avgBetAmount: N(g.avg_bet_amount),
          rtpPercentage: N(g.rtp_percentage),
        })),
      providesTags: ["Dashboard"],
    }),

    /* ========================================================== */
    /* NEW DEPOSITS ENDPOINT                                    */
    /* ========================================================== */
    getDeposits: builder.query({
      query: (params = {}) => {
        const queryParams = { ...params };

        // Handle text search for phone or receipt number
        if (params.search && params.search.trim()) {
          queryParams.search = params.search.trim();
        }

        // Handle specific filters
        if (params.paymentSource && params.paymentSource.trim()) {
          queryParams.payment_source = params.paymentSource.trim();
        }

        // Handle date range filters
        if (params.dateRange?.start && params.dateRange.end) {
          const { from, to } = adjustDateRange(params.dateRange.start, params.dateRange.end);
          queryParams.from = from;
          queryParams.to = to;
        } else if (params.quickFilter) {
          queryParams.period =
            {
              today: "daily",
              weekly: "weekly",
              monthly: "monthly",
              yearly: "yearly",
            }[params.quickFilter.toLowerCase()] || "all_time";
        }

        // Handle pagination
        if (params.limit) queryParams.limit = params.limit;
        if (params.offset) queryParams.offset = params.offset;

        console.log("Deposit search params:", queryParams);
        return `/data/deposits?${qs(queryParams)}`;
      },
      transformResponse: (res) => {
        console.log("Raw deposits API response:", res);

        if (!res) {
          console.warn("No response from deposits API");
          return {
            summary: { 
              recordsReturned: 0, 
              totalAmount: 0, 
              completedAmount: 0, 
              pendingAmount: 0,
              failedAmount: 0,
              uniqueUsers: 0, 
              averageDeposit: 0,
              successRate: 0,
              completedCount: 0,
              pendingCount: 0,
              failedCount: 0,
              statusBreakdown: {},
              providerBreakdown: {},
            },
            deposits: [],
          };
        }

        const transformedData = {
          summary: {
            // Counts
            recordsReturned: N(res?.summary?.records_returned),
            uniqueUsers: N(res?.summary?.unique_users),
            completedCount: N(res?.summary?.completed_count),
            pendingCount: N(res?.summary?.pending_count),
            failedCount: N(res?.summary?.failed_count),
            
            // Amounts
            totalAmount: N(res?.summary?.total_amount_in_results),
            completedAmount: N(res?.summary?.completed_amount),
            pendingAmount: N(res?.summary?.pending_amount),
            failedAmount: N(res?.summary?.failed_amount),
            averageDeposit: N(res?.summary?.average_deposit),
            
            // Rates
            successRate: N(res?.summary?.success_rate),
            
            // Breakdowns
            statusBreakdown: res?.summary?.status_breakdown || {},
            providerBreakdown: res?.summary?.provider_breakdown || {},
          },
          deposits: (res?.deposits || []).map((deposit) => ({
            // IDs
            id: deposit.id || "",
            user_id: deposit.user_id || "",
            deposit_id: deposit.deposit_id || "",
            session_id: deposit.session_id || "",
            provider_transaction_id: deposit.provider_transaction_id || "",
            
            // User info
            phone: deposit.phone || "N/A",
            username: deposit.username || "",
            
            // Money
            amount: N(deposit.amount),
            currency: deposit.currency || "ZMW",
            
            // Status & Type
            status: deposit.status || "UNKNOWN",
            deposit_type: deposit.deposit_type || "",
            provider: deposit.provider || "",
            
            // Location
            country: deposit.country || "",
            
            // M-PESA specific
            receipt_number: deposit.receipt_number || "",
            payment_source: deposit.payment_source || "",
            checkout_request_id: deposit.checkout_request_id || "",
            
            // Timestamps
            transaction_date: deposit.transaction_date || "",
            created_at: deposit.created_at || "",
            updated_at: deposit.updated_at || "",
            
            // Legacy fields (for backward compatibility)
            receiptNumber: deposit.receipt_number || "",
            paymentSource: deposit.payment_source || "",
            checkoutRequestId: deposit.checkout_request_id || "",
          })),
        };

        console.log("Transformed deposits data:", transformedData);
        return transformedData;
      },
      providesTags: ["Dashboard"],
    }),

    /* ========================================================== */
    /* NEW WITHDRAWALS ENDPOINT                                 */
    /* ========================================================== */
    getWithdrawals: builder.query({
      query: (params = {}) => {
        const queryParams = { ...params };

        // Handle text search for phone, receipt, etc.
        if (params.search && params.search.trim()) {
          queryParams.search = params.search.trim();
        }

        // Handle status filter
        if (params.status && params.status.trim()) {
          queryParams.status = params.status.trim();
        }

        // Handle date range filters
        if (params.dateRange?.start && params.dateRange.end) {
          const { from, to } = adjustDateRange(params.dateRange.start, params.dateRange.end);
          queryParams.from = from;
          queryParams.to = to;
        } else if (params.quickFilter) {
          queryParams.period =
            {
              today: "daily",
              weekly: "weekly",
              monthly: "monthly",
              yearly: "yearly",
            }[params.quickFilter.toLowerCase()] || "all_time";
        }

        // Handle pagination
        if (params.limit) queryParams.limit = params.limit;
        if (params.offset) queryParams.offset = params.offset;

        console.log("Withdrawal search params:", queryParams);
        // Ensure this matches your backend route for withdrawals
        return `/data/withdrawals?${qs(queryParams)}`;
      },
      transformResponse: (res) => {
        console.log("Raw withdrawals API response:", res);

        if (!res) {
          console.warn("No response from withdrawals API");
          return {
            summary: { 
              recordsReturned: 0, 
              totalAmount: 0, 
              completedAmount: 0, 
              pendingAmount: 0,
              acceptedAmount: 0,
              failedAmount: 0,
              uniqueUsers: 0, 
              averageWithdrawal: 0,
              successRate: 0,
              pendingCount: 0,
              acceptedCount: 0,
              completedCount: 0,
              failedCount: 0,
              rejectedCount: 0,
              statusBreakdown: {},
              payoutTypeBreakdown: {},
            },
            withdrawals: [],
          };
        }

        const transformedData = {
          summary: {
            // Counts
            recordsReturned: N(res?.summary?.records_returned),
            uniqueUsers: N(res?.summary?.unique_users),
            pendingCount: N(res?.summary?.pending_count),
            acceptedCount: N(res?.summary?.accepted_count),
            completedCount: N(res?.summary?.completed_count),
            failedCount: N(res?.summary?.failed_count),
            rejectedCount: N(res?.summary?.rejected_count),
            
            // Amounts
            totalAmount: N(res?.summary?.total_amount_in_results),
            completedAmount: N(res?.summary?.completed_amount),
            pendingAmount: N(res?.summary?.pending_amount),
            acceptedAmount: N(res?.summary?.accepted_amount),
            failedAmount: N(res?.summary?.failed_amount),
            averageWithdrawal: N(res?.summary?.average_withdrawal),
            
            // Rates
            successRate: N(res?.summary?.success_rate),
            
            // Breakdowns
            statusBreakdown: res?.summary?.status_breakdown || {},
            payoutTypeBreakdown: res?.summary?.payout_type_breakdown || {},
          },
          withdrawals: (res?.withdrawals || []).map((w) => ({
            // IDs
            id: w.id || "",
            user_id: w.user_id || "",
            payout_id: w.payout_id || "",
            session_id: w.session_id || "",
            provider_transaction_id: w.provider_transaction_id || "",
            transaction_id: w.transaction_id || "",
            
            // User info
            phone: w.phone || "N/A",
            username: w.username || "",
            
            // Money
            amount: N(w.amount),
            currency: w.currency || "ZMW",
            
            // Status & Type
            status: w.status || "pending",
            status_display: w.status_display || w.status || "Pending",
            payout_type: w.payout_type || "",
            provider: w.provider || "",
            
            // Location
            country: w.country || "",
            
            // Receipt info
            receipt: w.receipt || "",
            receipt_name: w.receipt_name || "",
            
            // Timestamps
            transaction_date: w.transaction_date || "",
            created_at: w.created_at || "",
            updated_at: w.updated_at || "",
            
            // Legacy fields (for backward compatibility)
            receiptName: w.receipt_name || "",
            
            // Computed fields for display
            displayId: w.payout_id || w.transaction_id || w.session_id || "",
          })),
        };

        console.log("Transformed withdrawals data:", transformedData);
        return transformedData;
      },
      providesTags: ["Dashboard"],
    }),

    getRecentBets: builder.query({
      query: (params = {}) => {
        const queryParams = { ...params };

        // Handle the search parameter
        if (params.search && params.search.trim()) {
          queryParams.search = params.search.trim();
        }

        // Handle service code filter
        if (params.service_code) {
          queryParams.service_code = params.service_code;
        }
        if (params.serviceCode && params.serviceCode.trim()) {
          queryParams.service_code = params.serviceCode.trim();
        }

        // Handle platform filter
        if (params.platform && params.platform.trim()) {
          queryParams.platform = params.platform.trim();
        }

        // Handle date range filters
        if (params.dateRange?.start && params.dateRange.end) {
          const { from, to } = adjustDateRange(params.dateRange.start, params.dateRange.end);
          queryParams.from = from;
          queryParams.to = to;
        } else if (params.quickFilter) {
          queryParams.period =
            {
              today: "daily",
              weekly: "weekly",
              monthly: "monthly",
              yearly: "yearly",
            }[params.quickFilter.toLowerCase()] || "all_time";
        }

        // Handle pagination
        if (params.limit) queryParams.limit = params.limit;
        if (params.offset) queryParams.offset = params.offset;

        console.log("Fetching recent bets with params:", queryParams);
        return `/data/recent?${qs(queryParams)}`;
      },
      /**
       * Transforms the complex paginated response from the server
       * into a structured object that the UI can easily use.
       */
      transformResponse: (r) => ({
        data: r?.data ?? [],
        pagination: {
          limit: N(r?.pagination?.limit, 100),
          offset: N(r?.pagination?.offset),
          total: N(r?.pagination?.total),
          hasMore: !!r?.pagination?.hasMore,
          currentPage: N(r?.pagination?.currentPage, 1),
          totalPages: N(r?.pagination?.totalPages, 1),
        },
        filters: r?.filters || {},
      }),
      providesTags: ["Dashboard", "Bets"], // Use specific tags for better caching
    }),

    // getRecentBets: builder.query({
    //   query: p => `/data/recent?${qs(p)}`,
    //   transformResponse: r => ({
    //     data: r?.data ?? [],
    //     pagination: {
    //       limit:        N(r?.pagination?.limit, 100),
    //       offset:       N(r?.pagination?.offset),
    //       total:        N(r?.pagination?.total),
    //       hasMore:      !!r?.pagination?.hasMore,
    //       currentPage:  N(r?.pagination?.currentPage, 1),
    //       totalPages:   N(r?.pagination?.totalPages, 1),
    //     },
    //     filters: r?.filters || {},
    //   }),
    //   providesTags: ['Dashboard'],
    // }),
  }),
});

export const {
  useGetMetricsQuery,
  useLazyGetMetricsQuery,
  useGetServiceCodesQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useSearchCustomerByPhoneQuery,
  useLazySearchCustomerByPhoneQuery,
  useGetRevenueBreakdownQuery,
  useGetGameStatsQuery,
  useGetRecentBetsQuery,
  useLazyGetRecentBetsQuery, // For triggering searches manually (e.g., on button click)
  useGetDepositsQuery, // <-- ADDED
  useLazyGetDepositsQuery, // <-- ADDED
  useGetWithdrawalsQuery, // <-- ADDED
  useLazyGetWithdrawalsQuery, // <-- ADDED
} = dashboardApi;
